import React from "react";
import { withStyles } from "@material-ui/styles";
import { I18n } from "@aws-amplify/core";
import { get, size, filter, sortBy, map } from "lodash";
import { Line } from "react-chartjs-2";
// eslint-disable-next-line
import ChartDataLabels from "chartjs-plugin-datalabels";
// eslint-disable-next-line
import ChartAnnotationsPlugin from "chartjs-plugin-annotation";

import Grid from "@material-ui/core/Grid";

import { formatDate } from "../../../utils/date";
import { getAttributeValue, getLovValue } from "../../../utils/profile";
import { getAttributeName } from "../../../utils/attributes";
import {
  getThreshold,
  getMeasureType,
  getDisplayedTemperatureAttr,
  getTemperatureByOperation,
} from "../../../utils/temperature";

import { BODY_TYPE, ISO_TYPE, MANUAL_TYPE } from "../../../constants/common";

import colors from "../../../assets/style/colors";
import images from "../../../assets/img/images";

const styles = {
  hideValues: {
    textAlign: "right",
    color: colors.primary,
    "& span": {
      cursor: "pointer",
    },
    "& span:hover": {
      textDecoration: "underline",
    },
  },
  colorCircle: {
    width: "14px",
    height: "14px",
    borderRadius: "50%",
  },
  legend: {
    marginLeft: "5px",
  },
};

const TemperatureGraph = (props) => {
  const { classes, data, startTime, lov } = props;

  const events82 = sortBy(
    filter(
      get(data, ["events"]),
      (e) => get(e, ["type", "id"]) === 82 && get(e, ["active"])
    ),
    ["created_on"]
  );

  const isoMeasures = sortBy(
    map(events82, (e) =>
      getMeasureType(e) === ISO_TYPE ? e : { created_on: e.created_on }
    ),
    ["created_on"]
  );

  const bodyMeasures = sortBy(
    map(events82, (e) =>
      getMeasureType(e) === BODY_TYPE ? e : { created_on: e.created_on }
    ),
    ["created_on"]
  );

  const manualMeasures = sortBy(
    map(events82, (e) =>
      getMeasureType(e) === MANUAL_TYPE ? e : { created_on: e.created_on }
    ),
    ["created_on"]
  );

  if (!get(data, ["events"]) || size(events82) === 0) {
    return (
      <Grid
        item
        xs
        className="text-54 flexCenter"
        style={{ justifyContent: "center" }}
      >
        {props.disableCurrent
          ? I18n.get("site_selectRowToViewDetails")
          : I18n.get("site_noTemperatureReading")}
      </Grid>
    );
  }

  const displayedTempAttrIso = getDisplayedTemperatureAttr(ISO_TYPE);
  const displayedTempAttrBody = getDisplayedTemperatureAttr(BODY_TYPE);
  const displayedTempAttrManual = getDisplayedTemperatureAttr(MANUAL_TYPE);

  const dataLine = {
    labels: map(
      sortBy(
        events82,
        (e) =>
          new Date(
            getMeasureType(e) === MANUAL_TYPE
              ? e.created_on
              : getAttributeValue(e, 13)
          )
      ),
      (e) =>
        startTime
          ? formatDate(
              new Date(
                getMeasureType(e) === MANUAL_TYPE
                  ? e.created_on
                  : getAttributeValue(e, 13)
              ) - new Date(startTime),
              "mm:ss"
            )
          : " "
    ),
    datasets: [
      // ISO Curve
      {
        label: "iso_curve",
        borderColor: colors.isoYellow,
        pointBackgroundColor: colors.isoYellow,
        pointBorderColor: colors.isoYellow,
        pointBorderWidth: 2,
        borderWidth: 1,
        data: isoMeasures.map((e) =>
          e
            ? parseFloat(
                parseFloat(getAttributeValue(e, displayedTempAttrIso)).toFixed(
                  1
                )
              )
            : null
        ),
        fill: false,
        lineTension: 0,
      },
      // Body curve
      {
        label: "body_curve",
        borderColor: colors.bodyPink,
        pointBackgroundColor: colors.bodyPink,
        pointBorderColor: colors.bodyPink,
        pointBorderWidth: 2,
        borderWidth: 1,
        data: bodyMeasures.map((e) =>
          e
            ? parseFloat(
                parseFloat(getAttributeValue(e, displayedTempAttrBody)).toFixed(
                  1
                )
              )
            : null
        ),
        fill: false,
        lineTension: 0,
      },
      // Manual curve
      {
        label: "manual_curve",
        borderColor: colors.manualTempBlue,
        pointBackgroundColor: colors.manualTempBlue,
        pointBorderColor: colors.manualTempBlue,
        pointBorderWidth: 2,
        borderWidth: 1,
        data: manualMeasures.map((e) =>
          e
            ? parseFloat(
                parseFloat(
                  getAttributeValue(e, displayedTempAttrManual)
                ).toFixed(1)
              )
            : null
        ),
        fill: false,
        lineTension: 0,
      },
    ],
  };

  const temperatureType =
    getLovValue(
      88,
      get(
        filter(get(events82, [0, "attributes"]), (attr) => attr.id === 88),
        [0, "value"]
      ),
      lov,
      "en"
    ) || "°C";

  const maxValue = getTemperatureByOperation(data, "max");
  const minValue = getTemperatureByOperation(data, "min");

  return (
    <>
      <Grid item style={{ margin: "10px 0px" }}>
        <Grid container alignItems="center">
          <Grid item className="flexCenter" style={{ marginLeft: "15px" }}>
            <img src={images.temperatureIcon} alt="temperature" />
          </Grid>
          <Grid item xs className="text-center">
            <Grid container justify="center" alignItems="center">
              <Grid
                item
                style={{ backgroundColor: colors.isoYellow }}
                className={classes.colorCircle}
              />
              <Grid
                item
                className={classes.legend}
                style={{ marginRight: "40px" }}
              >
                {getAttributeName(getDisplayedTemperatureAttr(ISO_TYPE))}
              </Grid>
              <Grid
                item
                style={{ backgroundColor: colors.bodyPink }}
                className={classes.colorCircle}
              />
              <Grid
                item
                className={classes.legend}
                style={{ marginRight: "40px" }}
              >
                {getAttributeName(getDisplayedTemperatureAttr(BODY_TYPE))}
              </Grid>
              <Grid
                item
                style={{ backgroundColor: colors.manualTempBlue }}
                className={classes.colorCircle}
              />
              <Grid item className={classes.legend}>
                {getAttributeName(getDisplayedTemperatureAttr(MANUAL_TYPE))}
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
      <Grid item>
        <Grid container wrap="nowrap">
          <Grid item xs>
            <Line
              data={dataLine}
              options={{
                spanGaps: true,
                responsive: true,
                legend: {
                  display: false,
                },
                tooltips: {
                  enabled: false,
                },
                layout: {
                  padding: {
                    right: 20,
                  },
                },
                annotation: {
                  annotations: [
                    {
                      type: "line",
                      mode: "horizontal",
                      scaleID: "y-axis-0",
                      value: getThreshold(data),
                      borderColor: colors.threatRed,
                      borderWidth: 2,
                      borderDash: [4, 2],
                      label: {
                        enabled: true,
                        position: "right",
                        content: `${getThreshold(data)}${temperatureType}`,
                        backgroundColor: "white",
                        fontColor: colors.threatRed,
                      },
                    },
                  ],
                },
                plugins: {
                  datalabels: {
                    display: true,
                    color: (context) =>
                      parseFloat(
                        get(context, [
                          "dataset",
                          "data",
                          get(context, ["dataIndex"]),
                        ])
                      ) > getThreshold(data)
                        ? colors.threatRed
                        : colors.cardGreen,
                    borderColor: (context) =>
                      get(context, [
                        "dataset",
                        "data",
                        get(context, ["dataIndex"]),
                      ]) > getThreshold(data)
                        ? colors.threatRed
                        : colors.cardGreen,
                    backgroundColor: (context) =>
                      get(context, [
                        "dataset",
                        "data",
                        get(context, ["dataIndex"]),
                      ]) > getThreshold(data)
                        ? colors.threatRed10
                        : colors.cardGreen10,
                    borderWidth: 1,
                    borderRadius: 2,
                    anchor: "end",
                    align: -90,
                  },
                },
                scales: {
                  xAxes: [
                    {
                      scaleLabel: {
                        display: false,
                        labelString: I18n.get("site_chrono"),
                      },
                    },
                  ],
                  yAxes: [
                    {
                      scaleLabel: {
                        display: false,
                        labelString: I18n.get("site_measuredTemp"),
                      },
                      ticks: {
                        stepSize: 1,
                        min: Math.min(
                          Math.round(minValue - 1),
                          Math.round(getThreshold(data) - 1)
                        ),
                        max: Math.max(
                          Math.round(maxValue + 1),
                          Math.round(getThreshold(data) + 1)
                        ),
                        padding: 8,
                        callback: (value) => `${value}${temperatureType}`,
                      },
                    },
                  ],
                },
              }}
            />
          </Grid>
          <Grid
            item
            style={{
              display: "flex",
              alignItems: "flex-end",
              padding: "0px 0px 6px 8px",
            }}
          >
            <img src={images.chronoIconBlack} alt="chrono" />
          </Grid>
        </Grid>
      </Grid>
    </>
  );
};

export default withStyles(styles)(TemperatureGraph);
